import { type FC, type MouseEvent, useCallback, useState, type ReactElement, useMemo } from 'react';

import { Drawer } from '@vakantiesnl/components/src/atoms/Drawer';
import { Modal } from '@vakantiesnl/components/src/atoms/Modal';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { AdlinksContainer } from '@vakantiesnl/components/src/molecules/AdlinksContainer';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import type { AdlinkValues } from '@vakantiesnl/services/src/gtm/adlinks';
import { useApplicationStore } from '@vakantiesnl/services/src/stores';
import { type MicroCopy, type Search, type Adlinks as AdlinkTypes } from '@vakantiesnl/types';

import { useCustomStyles } from './PriceCard.style';
import { BookableView, NonBookableView } from './views';
import { isZoover } from '../../utils';
import { isOfferWithZooverDeal, parseZooverDealDate } from '../../utils/accommodation/zooverDealHelpers';
import { ZooverDealBar } from '../ZooverDealBar';

export type PriceCardProps = {
	/** Copies used to display text */
	microCopies: MicroCopy;
	/** A boolean to define a bookable or non bookable picecard */
	isBookable: boolean;
	/** Adlinks to show */
	accommodationAdlinks?: AdlinkTypes.Adlink[];
	/** Accommodation view from redux */
	accommodation: Search.FullAccommodationItem;
	/** View CTA onClick tracking event */
	viewOffersEvent: (clickEvent: MouseEvent<HTMLButtonElement>) => void;
	/** View CTA onClick from the image modal */
	viewOffersFromImageModal?: (clickEvent: MouseEvent<HTMLButtonElement>) => void;
	/** Offer to show in the price card */
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer | null;
	/** Whether the fetching of current offer data is loading */
	offerIsLoading?: boolean;
	/** Whether there are offers available */
	hasOffers: boolean;
	/** Gallery is open */
	isGalleryOpen?: boolean;
	/** Image modal is open */
	isImageModalOpen?: boolean;
	/** Check if CTA is clicked */
	onClickNonBookableCta?: () => void;
	/** Function for closing the image modal */
	onImageModalClose?: () => void;
	/** Content of the Google Analytics cookie */
	googleAnalyticsCookie?: string;
	/** Wether the price of the pricecard is in the viewport */
	setIsPriceInView?: (inView: boolean) => void;
};

export const PriceCard: FC<PriceCardProps> = ({
	microCopies,
	isBookable,
	accommodationAdlinks,
	accommodation,
	viewOffersEvent,
	viewOffersFromImageModal,
	offer,
	offerIsLoading,
	isGalleryOpen,
	isImageModalOpen,
	onClickNonBookableCta,
	onImageModalClose,
	googleAnalyticsCookie,
	setIsPriceInView,
	hasOffers,
}) => {
	const { isDesktop } = useBreakpoints();
	const [isAdlinkPopperOpen, setIsAdlinkPopperOpen] = useState(false);
	const { classes } = useCustomStyles();
	const favorites = useApplicationStore((s) => s.favorites);
	const setFavorites = useApplicationStore((s) => s.setFavorites);

	const handleAdlinkCtaClick = useCallback(
		(clickEvent: MouseEvent<HTMLButtonElement>) => {
			if (isGalleryOpen && onClickNonBookableCta) {
				onClickNonBookableCta();
			}
			if (isImageModalOpen) {
				/** Close image modal when opening the adlink modal */
				onImageModalClose?.();
			}
			setIsAdlinkPopperOpen(true);
			viewOffersEvent(clickEvent);
		},
		[isGalleryOpen, isImageModalOpen, onClickNonBookableCta, onImageModalClose, viewOffersEvent],
	);

	const onAdlinkPopperClose = useCallback(() => {
		setIsAdlinkPopperOpen(false);
	}, []);

	const trackPriceCardAdlinks = useCallback(
		(adlinkValues: AdlinkValues) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/adlinks').then(({ trackAdlinksPriceComponent }) =>
					track(trackAdlinksPriceComponent(adlinkValues, accommodation)),
				),
			);
		},
		[accommodation],
	);
	const trackAdlinksPhotosModal = useCallback(
		(adlinkValues: AdlinkValues) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/adlinks').then(({ trackAdlinksPhotosModal }) =>
					track(trackAdlinksPhotosModal(adlinkValues, accommodation)),
				),
			);
		},
		[accommodation],
	);

	const trackFavoriteClick = useCallback(
		(isFavorite: boolean) => {
			import('@vakantiesnl/services/src/gtm/common').then(({ track }) =>
				import('@vakantiesnl/services/src/gtm/favorite').then(({ onFavoriteClick }) =>
					track(
						onFavoriteClick({
							position: 0,
							acco: accommodation,
							entity: {
								entity_level: 'destination',
								entity_id: '',
							},
							action: isFavorite ? 'deselect' : 'select',
							pageType: 'accommodation',
						}),
					),
				),
			);
		},
		[accommodation],
	);

	const isFavorite = useMemo(
		() => favorites.some((item) => item.entityId === accommodation.heliosId),
		[accommodation.heliosId, favorites],
	);

	const handleOnFavoriteClick = useCallback(() => {
		/** Track the clicking of fav/unfav */
		trackFavoriteClick(isFavorite);

		let updatedFavorites;

		if (isFavorite) {
			/** Remove favorite if the accommodation entityId is already in a favorite item */
			updatedFavorites = favorites.filter((item) => item.entityId !== accommodation.heliosId);
		} else {
			const newFavorite = {
				entityId: accommodation.heliosId,
				timeStamp: Date.now(),
			};
			updatedFavorites = [...favorites, newFavorite];
		}

		setFavorites(updatedFavorites);
	}, [accommodation.heliosId, favorites, isFavorite, setFavorites, trackFavoriteClick]);

	const partnerPopperContent = useMemo(
		(): ReactElement => (
			<AdlinksContainer
				adlinks={accommodationAdlinks as AdlinkTypes.Adlink[]}
				onToggleClick={handleAdlinkCtaClick}
				accommodationName={accommodation.title}
				toggleVariant="secondary"
				showExpanded={true}
				showDefaultDescription={false}
				descriptionToShow={
					<Typography variant="bodyMd" as="p">
						{microCopies['adlinks.popper.description']}
					</Typography>
				}
				onAdlinkClick={isImageModalOpen ? trackAdlinksPhotosModal : trackPriceCardAdlinks}
			/>
		),
		[
			accommodation.title,
			accommodationAdlinks,
			handleAdlinkCtaClick,
			isImageModalOpen,
			microCopies,
			trackAdlinksPhotosModal,
			trackPriceCardAdlinks,
		],
	);

	/** Only peakwork offers can have a zoover deal for now */
	const hasZooverDeal = isZoover && isBookable && isOfferWithZooverDeal(offer);

	return (
		<div className={classes.priceCard} data-cy="accommodation-PriceCard">
			{hasZooverDeal && (
				<div className={classes.zooverDealContainer} data-cy="zooverDealPriceCard">
					<ZooverDealBar
						title={microCopies['accoCard.flashdeal']}
						expirationDate={parseZooverDealDate(offer.expirationDate)}
						discountAmount={offer.discountAmount}
						discountType={offer.discountAmountType}
						variant="priceCard"
					/>
				</div>
			)}
			{isBookable && (
				<BookableView
					adlinks={accommodationAdlinks}
					offer={offer}
					hasOffers={hasOffers}
					offerIsLoading={offerIsLoading}
					viewOffersEvent={viewOffersFromImageModal ?? viewOffersEvent}
					isGalleryOpen={isGalleryOpen}
					isModalOpen={isImageModalOpen}
					handleAdlinkCtaClick={handleAdlinkCtaClick}
					onAdlinkClick={isImageModalOpen ? trackAdlinksPhotosModal : trackPriceCardAdlinks}
					setIsPriceInView={setIsPriceInView}
					accommodation={accommodation}
					isFavorite={isFavorite}
					onFavoriteClick={handleOnFavoriteClick}
				/>
			)}
			{!isBookable && (
				<NonBookableView
					accommodation={accommodation}
					adlinks={accommodationAdlinks}
					isGalleryOpen={isGalleryOpen}
					isImageModalOpen={isImageModalOpen}
					handleAdlinkCtaClick={handleAdlinkCtaClick}
					onAdlinkClick={isImageModalOpen ? trackAdlinksPhotosModal : trackPriceCardAdlinks}
					googleAnalyticsCookie={googleAnalyticsCookie}
					isFavorite={isFavorite}
					onFavoriteClick={handleOnFavoriteClick}
				/>
			)}
			{isDesktop ? (
				<Modal
					open={isAdlinkPopperOpen}
					title={microCopies['adlinks.hybridproductpage.heading']}
					hasCloseIcon={true}
					onModalClose={onAdlinkPopperClose}
				>
					{partnerPopperContent}
				</Modal>
			) : (
				<Drawer
					open={isAdlinkPopperOpen}
					direction="up"
					title={microCopies['adlinks.drawer.heading']}
					hasCloseIcon={true}
					onDrawerClose={onAdlinkPopperClose}
				>
					{partnerPopperContent}
				</Drawer>
			)}
		</div>
	);
};
